import React, { useState } from "react";
import { Loader2, Eye, EyeOff, Lock, KeyRound, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Password management card inside Settings → Безпека.
 *
 * Two modes, decided from `user.auth_providers`:
 *   • "set" — user signed up via Google only, no password yet.
 *     Form: just new_password. Calls POST /auth/set-password.
 *   • "change" — user already has a password.
 *     Form: old_password + new_password. Calls POST /auth/change-password.
 *
 * After a successful change, the backend revokes all OTHER sessions for
 * this user — so we re-fetch /auth/me to make sure the UI reflects any
 * fresh state, and toast a heads-up that "other devices were signed out".
 */
const PasswordCard = () => {
    const { user, refresh } = useAuth();
    const providers = user?.auth_providers || [];
    const hasPassword = providers.includes("password");

    return hasPassword
        ? <ChangePasswordForm onChanged={refresh} />
        : <SetPasswordForm onSet={refresh} />;
};

// ── Shared inline password input with show/hide toggle ────────────────────
const PasswordField = ({ id, label, value, onChange, autoComplete, hint, testid }) => {
    const [show, setShow] = useState(false);
    return (
        <div>
            <label htmlFor={id} className="block text-xs font-medium text-secondary-c mb-1.5">
                {label}
            </label>
            <div className="relative">
                <input
                    id={id}
                    type={show ? "text" : "password"}
                    autoComplete={autoComplete}
                    required
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={testid}
                    className="w-full h-10 rounded-md border border-border bg-surface px-3 pr-10 text-sm text-text-primary placeholder:text-faint-c focus:outline-none focus:border-primary transition"
                    placeholder="••••••••"
                />
                <button
                    type="button"
                    onClick={() => setShow(v => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-faint-c hover:text-text-primary transition"
                    tabIndex={-1}
                    aria-label={show ? "Сховати пароль" : "Показати пароль"}
                >
                    {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
            </div>
            {hint && <p className="mt-1.5 text-[11px] text-faint-c">{hint}</p>}
        </div>
    );
};

const PasswordHints = ({ password }) => {
    const hasMinLen = password.length >= 8;
    const hasLetter = /[A-Za-z]/.test(password);
    const hasDigit  = /\d/.test(password);
    return (
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
            <Hint ok={hasMinLen}>8+ символів</Hint>
            <Hint ok={hasLetter}>буква</Hint>
            <Hint ok={hasDigit}>цифра</Hint>
        </div>
    );
};

const Hint = ({ ok, children }) => (
    <span className={`inline-flex items-center gap-1 ${ok ? "text-success" : "text-faint-c"}`}>
        {ok ? <Check size={11} /> : <span className="inline-block w-1 h-1 rounded-full bg-faint-c" />}
        {children}
    </span>
);

// ── Mode 1: user has no password yet (Google-only) ────────────────────────
const SetPasswordForm = ({ onSet }) => {
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const hasMinLen = password.length >= 8;
    const hasLetter = /[A-Za-z]/.test(password);
    const hasDigit  = /\d/.test(password);
    const canSubmit = !submitting && hasMinLen && hasLetter && hasDigit;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setError(null);
        setSubmitting(true);
        try {
            await api.post("/auth/set-password", { new_password: password });
            toast.success("Пароль установлено. Тепер ви можете входити email + паролем.");
            setPassword("");
            try { await onSet(); } catch {}
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const status = err?.response?.status;
            if (status === 409) {
                setError("Пароль уже встановлено. Перезавантажте сторінку.");
            } else if (status === 422) {
                setError(typeof detail === "string" ? detail : "Перевірте пароль.");
            } else {
                setError(typeof detail === "string" ? detail : "Не вдалося встановити пароль.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="rounded-xl border border-border bg-bg-base p-5">
            <div className="flex items-start gap-3 mb-4">
                <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <KeyRound className="text-primary" size={18} />
                </div>
                <div className="min-w-0">
                    <div className="font-semibold text-sm text-text-primary">Установити пароль</div>
                    <div className="text-xs text-secondary-c mt-0.5 leading-relaxed">
                        Зараз ви входите тільки через Google. Установіть пароль як резервний спосіб входу.
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} data-testid="set-password-form" className="space-y-3">
                <PasswordField
                    id="set-new-password"
                    label="Новий пароль"
                    value={password}
                    onChange={setPassword}
                    autoComplete="new-password"
                    testid="set-password-input"
                />
                <PasswordHints password={password} />

                {error && (
                    <div className="rounded-md bg-error/5 border border-error/30 px-3 py-2 text-xs text-error" data-testid="set-password-error">
                        {error}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={!canSubmit}
                    data-testid="set-password-submit"
                    className="w-full h-10 rounded-md bg-primary text-on-primary font-medium text-sm transition disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 flex items-center justify-center gap-2"
                >
                    {submitting && <Loader2 className="animate-spin" size={16} />}
                    Установити пароль
                </button>
            </form>
        </div>
    );
};

// ── Mode 2: user already has password — let them change it ────────────────
const ChangePasswordForm = ({ onChanged }) => {
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const hasMinLen = newPassword.length >= 8;
    const hasLetter = /[A-Za-z]/.test(newPassword);
    const hasDigit  = /\d/.test(newPassword);
    const samePass  = oldPassword && newPassword && oldPassword === newPassword;
    const canSubmit = !submitting && oldPassword && hasMinLen && hasLetter && hasDigit && !samePass;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setError(null);
        setSubmitting(true);
        try {
            await api.post("/auth/change-password", {
                old_password: oldPassword,
                new_password: newPassword,
            });
            toast.success("Пароль змінено. Інші пристрої були вийшли з акаунту для безпеки.");
            setOldPassword("");
            setNewPassword("");
            try { await onChanged(); } catch {}
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const status = err?.response?.status;
            if (status === 401) {
                setError("Неправильний поточний пароль.");
            } else if (status === 422) {
                setError(typeof detail === "string" ? detail : "Перевірте новий пароль.");
            } else if (status === 429) {
                setError("Забагато спроб. Спробуйте пізніше.");
            } else {
                setError(typeof detail === "string" ? detail : "Не вдалося змінити пароль.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="rounded-xl border border-border bg-bg-base p-5">
            <div className="flex items-start gap-3 mb-4">
                <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Lock className="text-primary" size={18} />
                </div>
                <div className="min-w-0">
                    <div className="font-semibold text-sm text-text-primary">Змінити пароль</div>
                    <div className="text-xs text-secondary-c mt-0.5 leading-relaxed">
                        Після зміни всі інші пристрої будуть вийдені з акаунту.
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} data-testid="change-password-form" className="space-y-3">
                <PasswordField
                    id="cur-password"
                    label="Поточний пароль"
                    value={oldPassword}
                    onChange={setOldPassword}
                    autoComplete="current-password"
                    testid="change-password-old"
                />
                <PasswordField
                    id="new-password"
                    label="Новий пароль"
                    value={newPassword}
                    onChange={setNewPassword}
                    autoComplete="new-password"
                    testid="change-password-new"
                />
                <PasswordHints password={newPassword} />

                {samePass && (
                    <div className="rounded-md bg-warning/5 border border-warning/30 px-3 py-2 text-xs text-warning">
                        Новий пароль має відрізнятись від поточного.
                    </div>
                )}

                {error && (
                    <div className="rounded-md bg-error/5 border border-error/30 px-3 py-2 text-xs text-error" data-testid="change-password-error">
                        {error}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={!canSubmit}
                    data-testid="change-password-submit"
                    className="w-full h-10 rounded-md bg-primary text-on-primary font-medium text-sm transition disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 flex items-center justify-center gap-2"
                >
                    {submitting && <Loader2 className="animate-spin" size={16} />}
                    Змінити пароль
                </button>
            </form>
        </div>
    );
};

export default PasswordCard;
